import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { LocationEditor } from "../components/LocationEditor";
import { Disclaimer } from "../components/Disclaimer";
import { LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [hours, setHours] = useState(user?.hoursPerWeek ?? 0);
  const [cash, setCash] = useState(user?.monthlyCash ?? 0);
  const [units, setUnits] = useState(user?.units || "imperial");
  const [showLoc, setShowLoc] = useState(false);
  const [claimEmail, setClaimEmail] = useState("");
  const [claimPw, setClaimPw] = useState("");

  const saveProfile = async () => {
    try {
      await api.put("/profile", { hoursPerWeek: Number(hours), monthlyCash: Number(cash), units });
      await refresh(); toast.success("Saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteLoc = async () => { await api.delete("/location"); await refresh(); toast.success("Location data deleted"); };
  const deleteAccount = async () => {
    if (!window.confirm("Delete your account and all data? This cannot be undone.")) return;
    await api.delete("/account"); await logout(); nav("/");
  };
  const claim = async () => {
    try {
      await api.post("/auth/claim", { email: claimEmail, password: claimPw });
      await refresh(); toast.success("Account saved — you can sign in next time");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const place = user?.city ? `${user.city}${user.region ? ", " + user.region : ""}` : "Not set";

  return (
    <Layout>
      <div className="fadeup space-y-5 max-w-2xl">
        <h2 className="font-head font-bold text-2xl sm:text-3xl">Settings</h2>

        <div className="pm-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="account-card">
          <div>
            <div className="label-mono">Signed in as</div>
            <div className="text-sm mt-1">{user?.is_guest ? "Guest account" : user?.email}</div>
          </div>
          <button onClick={async () => { await logout(); nav("/"); }} className="pm-btn text-sm flex items-center gap-2" data-testid="settings-signout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {user?.role === "admin" && (
          <div className="pm-card p-4" data-testid="settings-admin-card">
            <div className="label-mono mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Admin</div>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>
              You're signed in as an admin. View every user on the platform, their subscription/plan, and permanently remove accounts.
            </p>
            <button onClick={() => nav("/admin")} className="pm-btn text-sm mt-3 flex items-center gap-2" data-testid="settings-admin-btn">
              <ShieldCheck className="w-4 h-4" /> Manage users
            </button>
          </div>
        )}

        {user?.is_guest && (
          <div className="pm-card p-4" data-testid="claim-account">
            <div className="label-mono mb-2" style={{ color: "var(--cost)" }}>Guest — save your account</div>
            <p className="text-sm mb-3" style={{ color: "var(--text-2)" }}>Add an email and password so you can come back to this path.</p>
            <div className="space-y-2">
              <input className="pm-input" type="email" placeholder="Email" value={claimEmail} onChange={(e) => setClaimEmail(e.target.value)} data-testid="claim-email" />
              <input className="pm-input" type="password" placeholder="Password (6+)" value={claimPw} onChange={(e) => setClaimPw(e.target.value)} data-testid="claim-password" />
              <button onClick={claim} className="pm-btn" data-testid="claim-btn">Save my account</button>
            </div>
          </div>
        )}

        <div className="pm-card p-4">
          <div className="label-mono mb-3">Details (optional)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">Free hours / week
              <input type="number" className="pm-input font-mono mt-1" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="settings-hours" /></label>
            <label className="text-sm">Monthly cash ($)
              <input type="number" className="pm-input font-mono mt-1" value={cash} onChange={(e) => setCash(e.target.value)} data-testid="settings-cash" /></label>
          </div>
          <div className="mt-3">
            <div className="label-mono mb-2">Units</div>
            <div className="flex gap-2">
              {["imperial", "metric"].map((u) => (
                <button key={u} onClick={() => setUnits(u)} className="badge" data-testid={`units-${u}`}
                  style={{ background: units === u ? "var(--text)" : "transparent", color: units === u ? "#fff" : "var(--text)" }}>{u}</button>
              ))}
            </div>
          </div>
          <button onClick={saveProfile} className="pm-btn mt-4" data-testid="save-profile-btn">Save</button>
        </div>

        <div className="pm-card p-4">
          <div className="label-mono mb-2">Location</div>
          <p className="text-sm">Current place: <b>{place}</b> <span className="label-mono">({user?.locationSource || "none"})</span></p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={() => setShowLoc(!showLoc)} className="pm-btn-ghost text-sm" data-testid="change-place-btn">Use a different place</button>
            <button onClick={deleteLoc} className="pm-btn-ghost text-sm" data-testid="delete-location-btn">Delete location data</button>
          </div>
          {showLoc && <div className="mt-3"><LocationEditor onDone={() => setShowLoc(false)} /></div>}
        </div>

        <div className="pm-card p-4" data-testid="support-card">
          <div className="label-mono mb-2">Help & support</div>
          <p className="text-sm mb-3">Questions, problems, or feedback? We're happy to help.</p>
          <div className="flex gap-2 flex-wrap">
            <a href="mailto:pathmentorai@gmail.com?subject=Path%20Mentor%20Support" className="pm-btn text-sm" data-testid="contact-support-btn">Contact support</a>
            <a href="tel:+18575203500" className="pm-btn-ghost text-sm" data-testid="support-phone-btn">Call (857) 520-3500</a>
            <a href="/privacy" target="_blank" rel="noreferrer" className="pm-btn-ghost text-sm" data-testid="privacy-link">Privacy Policy</a>
          </div>
        </div>

        <div className="pm-card p-4" style={{ borderColor: "var(--warning)" }}>
          <div className="label-mono mb-2" style={{ color: "var(--warning)" }}>Danger zone</div>
          <button onClick={deleteAccount} className="pm-btn-ghost text-sm" style={{ borderColor: "var(--warning)", color: "var(--warning)" }} data-testid="delete-account-btn">Delete account & all data</button>
        </div>

        <Disclaimer />
      </div>
    </Layout>
  );
}
